const UNITS = ['KB', 'MB', 'GB', 'TB', 'PB'] as const;

/** Human readable byte size (binary units, 1024 based). */
export function formatBytes(bytes: number, decimals?: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${Math.round(bytes)} B`;

  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }

  const digits = decimals ?? (value >= 100 ? 0 : value >= 10 ? 1 : 2);
  return `${value.toFixed(digits)} ${UNITS[unit] ?? 'PB'}`;
}

/** Bytes per second. */
export function formatSpeed(bytesPerSecond: number): string {
  if (!Number.isFinite(bytesPerSecond) || bytesPerSecond <= 0) return '—';
  return `${formatBytes(bytesPerSecond)}/s`;
}

/** Compact duration: 850ms, 42s, 3m 12s, 1h 24m. */
export function formatDuration(milliseconds: number): string {
  if (!Number.isFinite(milliseconds) || milliseconds < 0) return '—';
  if (milliseconds < 1000) return `${Math.round(milliseconds)}ms`;

  const totalSeconds = Math.floor(milliseconds / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;

  const totalMinutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (totalMinutes < 60) return seconds === 0 ? `${totalMinutes}m` : `${totalMinutes}m ${seconds}s`;

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours < 24) return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;

  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
}

/** Remaining time, capped so wildly wrong estimates do not confuse users. */
export function formatEta(milliseconds: number): string {
  if (!Number.isFinite(milliseconds) || milliseconds <= 0) return '—';
  if (milliseconds < 1000) return '<1s';
  return formatDuration(milliseconds);
}

export function formatPercent(ratio: number, decimals = 0): string {
  if (!Number.isFinite(ratio)) return '0%';
  const clamped = Math.min(1, Math.max(0, ratio));
  return `${(clamped * 100).toFixed(decimals)}%`;
}

export type RelativeTimeStyle = 'short' | 'long';

/** "just now", "3m ago", "in 2h", or an absolute date for older values. */
export function formatRelativeTime(
  timestamp: number,
  now: number = Date.now(),
  style: RelativeTimeStyle = 'short',
): string {
  const delta = timestamp - now;
  const absolute = Math.abs(delta);
  const future = delta > 0;

  const suffix = (value: string) =>
    style === 'long'
      ? future
        ? `in ${value}`
        : `${value} ago`
      : future
        ? `in ${value}`
        : `${value} ago`;

  if (absolute < 10_000) return 'just now';
  if (absolute < 60_000) return suffix(`${Math.floor(absolute / 1000)}s`);
  if (absolute < 3_600_000) return suffix(`${Math.floor(absolute / 60_000)}m`);
  if (absolute < 86_400_000) return suffix(`${Math.floor(absolute / 3_600_000)}h`);
  if (absolute < 7 * 86_400_000) return suffix(`${Math.floor(absolute / 86_400_000)}d`);

  const date = new Date(timestamp);
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() === new Date(now).getFullYear() ? undefined : 'numeric',
  }).format(date);
}

/** "14:03" style clock label for transfer history. */
export function formatTime(timestamp: number): string {
  return new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(timestamp));
}

/** "Aug 12, 14:03" for history rows. */
export function formatDateTime(timestamp: number): string {
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(timestamp));
}

/** Shorten long names from the middle, preserving the extension. */
export function truncateMiddle(value: string, maxLength = 40): string {
  if (value.length <= maxLength) return value;
  const head = Math.ceil((maxLength - 1) / 2);
  const tail = Math.floor((maxLength - 1) / 2);
  return `${value.slice(0, head)}…${value.slice(value.length - tail)}`;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function pluralize(count: number, singular: string, plural?: string): string {
  return count === 1 ? singular : (plural ?? `${singular}s`);
}
